class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;

    this.recommendationsTimer = null;

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    this.bindOverlay();

    this.setHeaderCartIconAccessibility();

    this.bindRecommendationActions();

    window.addEventListener('resize', () => {
      if (window.innerWidth < 990) {
        this.hideRecommendations();
      }
    });
  }


  /* ==========================================================
     OVERLAY
     ========================================================== */

  bindOverlay() {
    const overlay = this.querySelector(
      '#CartDrawer-Overlay'
    );

    if (!overlay) return;

    overlay.addEventListener(
      'click',
      this.close.bind(this)
    );
  }


  /* ==========================================================
     HEADER / ALL CART LINKS
     ========================================================== */

  setHeaderCartIconAccessibility() {
    const nativeCartLink = document.querySelector(
      '#cart-icon-bubble'
    );

    if (nativeCartLink) {
      nativeCartLink.setAttribute(
        'role',
        'button'
      );

      nativeCartLink.setAttribute(
        'aria-haspopup',
        'dialog'
      );

      nativeCartLink.setAttribute(
        'aria-expanded',
        'false'
      );

      nativeCartLink.addEventListener(
        'keydown',
        (event) => {
          if (
            event.code.toUpperCase() === 'SPACE' ||
            event.code.toUpperCase() === 'ENTER'
          ) {
            event.preventDefault();

            this.open(nativeCartLink);
          }
        }
      );
    }


    /*
      Catch any custom header link that points to /cart.
    */

    document.addEventListener(
      'click',
      (event) => {
        const cartLink =
          event.target.closest('a[href]');

        if (!cartLink) {
          return;
        }

        /*
          Do not intercept links inside the drawer.
        */

        if (this.contains(cartLink)) {
          return;
        }

        let url;

        try {
          url = new URL(
            cartLink.href,
            window.location.origin
          );
        } catch (error) {
          return;
        }

        if (
          url.origin !==
          window.location.origin
        ) {
          return;
        }

        const path =
          url.pathname.replace(/\/+$/, '');

        const isCartLink =
          path === '/cart' ||
          path.endsWith('/cart');

        if (!isCartLink) {
          return;
        }

        event.preventDefault();

        this.open(cartLink);
      }
    );
  }


  /* ==========================================================
     OPEN
     ========================================================== */

  open(triggeredBy) {
    if (this.classList.contains('active')) {
      return;
    }

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }


    /*
      Ensure recommendations always start hidden.
    */

    this.hideRecommendations();


    /*
      Drawer slide-in.
    */

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add(
          'animate',
          'active'
        );

        const cartLink =
          document.querySelector(
            '#cart-icon-bubble'
          );

        if (cartLink) {
          cartLink.setAttribute(
            'aria-expanded',
            'true'
          );
        }
      });
    });


    document.body.classList.add(
      'overflow-hidden'
    );


    const inner = this.querySelector(
      '.drawer__inner'
    );

    if (inner) {
      const handleTransitionEnd = () => {
        const containerToTrapFocusOn =
          document.getElementById(
            'CartDrawer'
          );

        const focusElement =
          this.querySelector(
            '.drawer__close'
          ) ||
          this.querySelector(
            '.drawer__inner'
          );

        if (
          containerToTrapFocusOn &&
          focusElement &&
          typeof trapFocus === 'function'
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      };

      inner.addEventListener(
        'transitionend',
        handleTransitionEnd,
        {
          once: true
        }
      );
    }


    this
      .querySelector('cart-drawer-items')
      ?.dispatchViewEvent?.();


    /*
      Open "You May Also Like" after 3 seconds.
    */

    this.scheduleRecommendations();
  }


  /* ==========================================================
     CLOSE
     ========================================================== */

  close() {
    this.hideRecommendations();

    this.classList.remove(
      'active'
    );

    const cartLink =
      document.querySelector(
        '#cart-icon-bubble'
      );

    if (cartLink) {
      cartLink.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    if (
      typeof removeTrapFocus === 'function'
    ) {
      removeTrapFocus(
        this.activeElement
      );
    }

    document.body.classList.remove(
      'overflow-hidden'
    );
  }


  /* ==========================================================
     RECOMMENDATION PANEL TIMER
     ========================================================== */

  scheduleRecommendations() {
    this.clearRecommendationsTimer();

    const panel = this.querySelector(
      '[data-voise-recommendations]'
    );

    if (!panel) {
      return;
    }

    const cards = panel.querySelectorAll(
      '[data-voise-rec-card]'
    );

    if (!cards.length) {
      return;
    }

    /*
      Desktop only.
    */

    if (window.innerWidth < 990) {
      return;
    }

    this.recommendationsTimer =
      window.setTimeout(
        () => {
          if (
            !this.classList.contains('active') ||
            window.innerWidth < 990
          ) {
            return;
          }

          this.classList.add(
            'recommendations-open'
          );

          panel.setAttribute(
            'aria-hidden',
            'false'
          );
        },
        3000
      );
  }


  /* ==========================================================
     HIDE RECOMMENDATIONS
     ========================================================== */

  hideRecommendations() {
    this.clearRecommendationsTimer();

    this.classList.remove(
      'recommendations-open'
    );

    const panel = this.querySelector(
      '[data-voise-recommendations]'
    );

    if (panel) {
      panel.setAttribute(
        'aria-hidden',
        'true'
      );
    }
  }


  /* ==========================================================
     TIMER CLEANUP
     ========================================================== */

  clearRecommendationsTimer() {
    if (this.recommendationsTimer) {
      window.clearTimeout(
        this.recommendationsTimer
      );

      this.recommendationsTimer = null;
    }
  }


  /* ==========================================================
     RECOMMENDATION ADD TO CART
     Event delegation survives AJAX drawer replacement.
     ========================================================== */

  bindRecommendationActions() {
    this.addEventListener(
      'click',
      (event) => {
        const button =
          event.target.closest(
            '[data-voise-rec-add]'
          );

        if (!button) {
          return;
        }

        event.preventDefault();

        this.addRecommendedProduct(
          button
        );
      }
    );
  }


  /* ==========================================================
     AJAX ADD RECOMMENDED PRODUCT
     ========================================================== */

  async addRecommendedProduct(button) {
    if (
      button.disabled ||
      button.getAttribute(
        'aria-busy'
      ) === 'true'
    ) {
      return;
    }

    const variantId =
      Number(
        button.dataset.variantId
      );

    if (!variantId) {
      return;
    }

    const buttonText =
      button.querySelector(
        '[data-voise-rec-add-text]'
      );

    const originalText =
      buttonText
        ? buttonText.textContent
        : (this.dataset.voiseAddLabel || 'ADD TO CART +');


    button.disabled = true;

    button.setAttribute(
      'aria-busy',
      'true'
    );

    button.classList.add(
      'is-loading'
    );

    if (buttonText) {
      buttonText.textContent =
        this.dataset.voiseAdding || 'ADDING...';
    }


    try {
      const formData =
        new FormData();

      formData.append(
        'id',
        String(variantId)
      );

      formData.append(
        'quantity',
        '1'
      );

      formData.append(
        'sections',
        this
          .getSectionsToRender()
          .map(
            (section) =>
              section.id
          )
      );

      formData.append(
        'sections_url',
        window.location.pathname
      );


      const response =
        await fetch(
          window.routes?.cart_add_url ||
            '/cart/add.js',
          {
            method: 'POST',

            headers: {
              Accept:
                'application/javascript',

              'X-Requested-With':
                'XMLHttpRequest'
            },

            body: formData
          }
        );


      const parsedState =
        await response.json();


      if (!response.ok) {
        throw new Error(
          parsedState.description ||
          parsedState.message ||
          this.dataset.voiseUnableToAdd || 'Unable to add product.'
        );
      }


      /*
        Hide current recommendation panel before
        drawer HTML gets refreshed.
      */

      this.hideRecommendations();


      /*
        Native Dawn update:
        - items
        - total
        - cart bubble
        - recommendation list
      */

      this.renderContents(
        parsedState
      );

    } catch (error) {
      console.error(
        'Voise recommendation add error:',
        error
      );

      button.disabled = false;

      button.removeAttribute(
        'aria-busy'
      );

      button.classList.remove(
        'is-loading'
      );

      if (buttonText) {
        buttonText.textContent =
          this.dataset.voiseTryAgain || 'TRY AGAIN';

        window.setTimeout(
          () => {
            buttonText.textContent =
              originalText;
          },
          1600
        );
      }
    }
  }


  /* ==========================================================
     DETAILS ACCESSIBILITY
     ========================================================== */

  setSummaryAccessibility(
    cartDrawerNote
  ) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    if (
      cartDrawerNote.nextElementSibling &&
      cartDrawerNote.nextElementSibling.getAttribute(
        'id'
      )
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        const details =
          event.currentTarget.closest(
            'details'
          );

        event.currentTarget.setAttribute(
          'aria-expanded',
          details
            ? !details.hasAttribute(
                'open'
              )
            : false
        );
      }
    );

    if (
      cartDrawerNote.parentElement &&
      typeof onKeyUpEscape ===
        'function'
    ) {
      cartDrawerNote.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }


  /* ==========================================================
     AJAX CART CONTENT UPDATE
     Used by CLAIM OFFER / ProductForm.
     ========================================================== */

  renderContents(parsedState) {
    this.productId =
      parsedState.id;


    /*
      Detect empty/non-empty state.
    */

    if (
      parsedState.sections &&
      parsedState.sections[
        'cart-drawer'
      ]
    ) {
      const parsedDrawer =
        this.getSectionDOM(
          parsedState.sections[
            'cart-drawer'
          ],
          'cart-drawer'
        );

      if (parsedDrawer) {
        this.classList.toggle(
          'is-empty',
          parsedDrawer.classList.contains(
            'is-empty'
          )
        );
      }
    }


    /*
      Replace cart drawer + cart bubble.
    */

    this
      .getSectionsToRender()
      .forEach(
        (section) => {
          const sectionElement =
            section.selector
              ? document.querySelector(
                  section.selector
                )
              : document.getElementById(
                  section.id
                );

          if (!sectionElement) {
            return;
          }

          const sectionHTML =
            parsedState.sections?.[
              section.id
            ];

          if (!sectionHTML) {
            return;
          }

          const innerHTML =
            this.getSectionInnerHTML(
              sectionHTML,
              section.selector
            );

          if (
            typeof innerHTML ===
            'string'
          ) {
            sectionElement.innerHTML =
              innerHTML;
          }
        }
      );


    /*
      #CartDrawer was replaced, so the overlay
      is also a new DOM element.
    */

    window.setTimeout(
      () => {
        this.bindOverlay();

        this.hideRecommendations();


        /*
          If drawer was already open
          (e.g. recommendation Add to Cart),
          do not try to open it again.
        */

        if (
          this.classList.contains(
            'active'
          )
        ) {
          this.scheduleRecommendations();
        } else {
          this.open();
        }
      },
      0
    );
  }


  /* ==========================================================
     GET INNER HTML
     ========================================================== */

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const element =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        )
        .querySelector(
          selector
        );

    return element
      ? element.innerHTML
      : '';
  }


  /* ==========================================================
     SECTIONS USED BY DAWN
     ========================================================== */

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }


  /* ==========================================================
     GET DOM
     ========================================================== */

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(
        selector
      );
  }


  /* ==========================================================
     ACTIVE ELEMENT
     ========================================================== */

  setActiveElement(element) {
    this.activeElement =
      element;
  }
}


/* ============================================================
   REGISTER CART DRAWER
   ============================================================ */

if (
  !customElements.get(
    'cart-drawer'
  )
) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}


/* ============================================================
   CART DRAWER ITEMS
   Native Dawn quantity / remove.
   ============================================================ */

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}


/* ============================================================
   REGISTER CART DRAWER ITEMS
   ============================================================ */

if (
  !customElements.get(
    'cart-drawer-items'
  )
) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}